import { Scale, yearTicks } from './scales.js';
import { svgEl } from './svg.js';

export type ChartMode = 'compact' | 'full';

export type ModeConfig = {
  width: number;
  height: number;
  margin: { top: number; right: number; bottom: number; left: number };
  maxXLabels: number;
  yTicks: number;
  fontSize: number;
};

export const MODES: Record<ChartMode, ModeConfig> = {
  compact: {
    width: 640,
    height: 260,
    margin: { top: 12, right: 16, bottom: 28, left: 56 },
    maxXLabels: 6,
    yTicks: 4,
    fontSize: 11,
  },
  full: {
    width: 1200,
    height: 560,
    margin: { top: 20, right: 24, bottom: 40, left: 80 },
    maxXLabels: 12,
    yTicks: 6,
    fontSize: 13,
  },
};

/** Chart surface colour, exposed as `--chart-surface` on the chart root (rings, separators). */
export const SURFACE = 'var(--chart-surface)';
export const GRID_STROKE = 'rgba(255, 255, 255, 0.06)';
export const AXIS_STROKE = 'rgba(255, 255, 255, 0.14)';
export const HAIRLINE_STROKE = 'rgba(255, 255, 255, 0.22)';

/** One empty state for every chart. */
export const EMPTY_TEXT = 'Заполните параметры кредита — график появится здесь.';

/**
 * Chart-only colour variables set on the chart root. `--series-current`, `--series-baseline`
 * and `--series-extra-1…5` live in `:root` (public/styles.css) because the comparison table
 * shares them.
 */
export const SERIES_VARS: Record<string, string> = {
  '--chart-surface': '#0c0a12',
  '--series-interest': 'var(--ember)',
  '--series-principal': 'var(--lime)',
  '--series-overpayment': '#5ad1ff',
};

export type PlotArea = { x0: number; x1: number; y0: number; y1: number };

export type Frame = {
  svg: SVGSVGElement;
  cfg: ModeConfig;
  width: number;
  height: number;
  plot: PlotArea;
  fontSize: number;
};

export type LegendEntry = { label: string; color: string; kind: 'line' | 'area' | 'dashed' };

/** Legend above the plot; returns null (and renders nothing) for fewer than two entries. */
export function renderLegend(entries: LegendEntry[]): HTMLElement | null {
  if (entries.length < 2) return null;
  const legend = document.createElement('div');
  legend.className = 'chart-legend';
  legend.setAttribute('aria-hidden', 'true');
  entries.forEach(entry => {
    const item = document.createElement('span');
    item.className = 'chart-legend-item';
    const swatch = document.createElement('span');
    swatch.className = `chart-legend-swatch is-${entry.kind}`;
    swatch.style.setProperty('--swatch-color', entry.color);
    const label = document.createElement('span');
    label.textContent = entry.label;
    item.append(swatch, label);
    legend.append(item);
  });
  return legend;
}

export type FrameOptions = {
  ariaLabel: string;
  legend?: LegendEntry[];
  /** Extra vertical space above the plot (e.g. for an event marker row). */
  topExtra?: number;
  /** Y-axis labels; the left margin grows so the widest one fits. */
  yLabels?: string[];
};

/** Left margin that fits every y label (never narrower than the mode default). */
export function measureLeftMargin(yLabels: string[], mode: ChartMode): number {
  const cfg = MODES[mode];
  const widest = yLabels.reduce((max, label) => Math.max(max, estimateTextWidth(label, cfg.fontSize)), 0);
  return Math.max(cfg.margin.left, Math.ceil(widest) + 14);
}

/** Smallest rendered height that is trusted as the viewBox height. */
export const MIN_MEASURED_HEIGHT = 120;
/** viewBox height for canvases that are rendered but too small to be a real layout. */
const FALLBACK_HEIGHT = 160;

/**
 * viewBox height for a measured svg height: the measured value whenever it is a plausible
 * layout (≥ 120 px), so `meet` never letterboxes the chart — on phones the payment chart
 * gets ≈ 150 px under its four-item legend. Zero (not laid out) falls back to the mode
 * default; a rendered but tiny height gets a floor so the plot stays usable.
 */
export function viewBoxHeight(measured: number, modeDefault: number): number {
  const rounded = Math.round(measured);
  if (rounded >= MIN_MEASURED_HEIGHT) return rounded;
  return rounded > 0 ? FALLBACK_HEIGHT : modeDefault;
}

/**
 * Clears the container, applies the series colour variables, mounts the legend and the SVG
 * and returns the plot geometry. The viewBox width follows the rendered width of the SVG so
 * text keeps its nominal pixel size; it falls back to the mode default when unmeasurable.
 */
export function createFrame(container: HTMLElement, mode: ChartMode, options: FrameOptions): Frame {
  const cfg = MODES[mode];
  container.replaceChildren();
  container.classList.add('chart-root');
  container.dataset.mode = mode;
  Object.entries(SERIES_VARS).forEach(([name, value]) => container.style.setProperty(name, value));

  const legend = renderLegend(options.legend ?? []);
  if (legend) container.append(legend);

  const svg = svgEl('svg', {
    width: '100%',
    height: '100%',
    preserveAspectRatio: 'xMidYMid meet',
    role: 'img',
    'aria-label': options.ariaLabel,
    tabindex: 0,
    'font-size': cfg.fontSize,
  });
  container.append(svg);

  const rect = svg.getBoundingClientRect();
  // 240 px keeps phone-width canvases (≈ 266 px at 390 px viewports) rendering at 1:1.
  const width = Math.max(240, Math.round(rect.width > 0 ? rect.width : cfg.width));
  const height = viewBoxHeight(rect.height, cfg.height);
  svg.setAttribute('viewBox', `0 0 ${width} ${height}`);

  const plot: PlotArea = {
    x0: measureLeftMargin(options.yLabels ?? [], mode),
    x1: width - cfg.margin.right,
    y0: cfg.margin.top + (options.topExtra ?? 0),
    y1: height - cfg.margin.bottom,
  };

  return { svg, cfg, width, height, plot, fontSize: cfg.fontSize };
}

function axisText(x: number, y: number, text: string, anchor: 'start' | 'middle' | 'end', fontSize: number): SVGTextElement {
  return svgEl('text', {
    x: x.toFixed(1),
    y: y.toFixed(1),
    'text-anchor': anchor,
    'font-size': fontSize,
    fill: 'var(--text-muted)',
    class: 'chart-axis-text',
  }, [text]);
}

/** Horizontal gridlines + right-aligned labels for the given tick values. */
export function drawYAxis(frame: Frame, ticks: number[], yScale: Scale, format: (value: number) => string): void {
  const grid = svgEl('g', { class: 'chart-grid' });
  const labels = svgEl('g', { class: 'chart-axis chart-axis-y' });
  ticks.forEach(tick => {
    const y = yScale(tick);
    grid.append(svgEl('line', {
      x1: frame.plot.x0,
      x2: frame.plot.x1,
      y1: y.toFixed(1),
      y2: y.toFixed(1),
      stroke: GRID_STROKE,
      'stroke-width': 1,
    }));
    labels.append(axisText(frame.plot.x0 - 8, y + frame.fontSize * 0.35, format(tick), 'end', frame.fontSize));
  });
  frame.svg.append(grid, labels);
}

export type XAxisOptions = {
  /** Position of month index `i` on the x axis. */
  xForIndex: (index: number) => number;
};

/** Baseline + year labels along the bottom of the plot. */
export function drawXAxis(frame: Frame, months: ReadonlyArray<{ dateLabel: string }>, options: XAxisOptions): void {
  const axis = svgEl('g', { class: 'chart-axis chart-axis-x' });
  axis.append(svgEl('line', {
    x1: frame.plot.x0,
    x2: frame.plot.x1,
    y1: frame.plot.y1.toFixed(1),
    y2: frame.plot.y1.toFixed(1),
    stroke: AXIS_STROKE,
    'stroke-width': 1,
  }));
  // Narrow canvases (phones) get fewer year labels than the mode allows so they never overlap.
  const slot = estimateTextWidth('2026', frame.fontSize) + 16;
  const fit = Math.max(2, Math.floor((frame.plot.x1 - frame.plot.x0) / slot));
  const indices = yearTicks(months, Math.min(frame.cfg.maxXLabels, fit));
  const labelY = frame.plot.y1 + frame.fontSize + 9;
  indices.forEach(index => {
    const month = months[index];
    if (!month) return;
    const x = options.xForIndex(index);
    const label = month.dateLabel.endsWith('-01') ? month.dateLabel.slice(0, 4) : month.dateLabel;
    axis.append(svgEl('line', {
      x1: x.toFixed(1),
      x2: x.toFixed(1),
      y1: frame.plot.y1.toFixed(1),
      y2: (frame.plot.y1 + 4).toFixed(1),
      stroke: AXIS_STROKE,
      'stroke-width': 1,
    }));
    // Labels at the plot edges are anchored inwards so they never run into the
    // bottom y label («0 ₽») or past the right edge.
    const half = estimateTextWidth(label, frame.fontSize) / 2;
    const anchor = x - half < frame.plot.x0 ? 'start' : x + half > frame.plot.x1 ? 'end' : 'middle';
    axis.append(axisText(x, labelY, label, anchor, frame.fontSize));
  });
  frame.svg.append(axis);
}

/** Estimated rendered width of a label in the monospace axis font. */
export function estimateTextWidth(text: string, fontSize: number): number {
  return text.length * fontSize * 0.6;
}

export type LabelItem = { x: number; text: string };
export type LabelPlacement = { row: number; anchor: 'start' | 'end'; x: number };

/**
 * Places short labels next to their x positions without overlap: labels that would collide
 * on a row move to the next row (up to `maxRows`); labels that would run past the right edge
 * are anchored at their end.
 */
export function assignLabelRows(items: LabelItem[], fontSize: number, plot: PlotArea, maxRows = 3): LabelPlacement[] {
  const gap = 6;
  const spacing = 8;
  const order = items.map((item, index) => ({ item, index })).sort((a, b) => a.item.x - b.item.x);
  const rowEnds: number[] = [];
  const placements: LabelPlacement[] = new Array(items.length);
  order.forEach(({ item, index }) => {
    const width = estimateTextWidth(item.text, fontSize);
    const fitsRight = item.x + gap + width <= plot.x1;
    const fitsLeft = item.x - gap - width >= 0;
    // Prefer the right side; fall back to the left; when neither fits (narrow canvases)
    // hug the right edge of the plot so the label stays inside the svg.
    const anchor: 'start' | 'end' = fitsRight ? 'start' : 'end';
    const anchorX = fitsRight ? item.x + gap : fitsLeft ? item.x - gap : plot.x1;
    const start = fitsRight ? anchorX : anchorX - width;
    const end = start + width;
    let row = rowEnds.findIndex(rowEnd => rowEnd + spacing <= start);
    if (row === -1) {
      if (rowEnds.length < maxRows) {
        row = rowEnds.length;
        rowEnds.push(end);
      } else {
        row = rowEnds.indexOf(Math.min(...rowEnds));
        rowEnds[row] = end;
      }
    } else {
      rowEnds[row] = end;
    }
    placements[index] = { row, anchor, x: anchorX };
  });
  return placements;
}

/** Replaces the container content with a muted message (used when there is nothing to plot). */
export function renderEmpty(container: HTMLElement, text: string): void {
  const message = document.createElement('p');
  message.className = 'chart-empty';
  message.textContent = text;
  container.replaceChildren(message);
}

/** Circle marker (≥ 8 px) with a 2 px surface-coloured ring. */
export function marker(x: number, y: number, color: string, radius = 4): SVGCircleElement {
  return svgEl('circle', {
    cx: x.toFixed(1),
    cy: y.toFixed(1),
    r: radius,
    fill: color,
    stroke: SURFACE,
    'stroke-width': 2,
  });
}
